/** Discount applied to mid-band price for the “faster sale” chip (15%). */
export const SELLER_BAND_QUICK_SALE_DISCOUNT_RATE = 0.15;

export type BandPriceChipTier = "standard" | "sell_sooner" | "sell_fastest";

export type BandRecommendationPrices = {
  /** Center of typical range (Standard tier). */
  midBandPrice: number;
  /** Mid minus 15% (Sell fastest tier). */
  fasterSalePrice: number;
  /** Low end of typical range = band min (Sell sooner tier). */
  lowestTypicalPrice: number;
};

export type BandPriceChipOption = {
  tier: BandPriceChipTier;
  price: number;
  isRecommended: boolean;
};

const RECOMMENDED_TIER_PRIORITY: BandPriceChipTier[] = [
  "sell_fastest",
  "sell_sooner",
  "standard",
];

/** Round to nearest ₦1,000 for cleaner chip prices (e.g. 212,850 → 213,000). */
function roundToNearestThousand(value: number): number {
  return Math.round(value / 1000) * 1000;
}

/**
 * Typical = band center; lowest typical = band min; faster sale = mid − 15%.
 * All chip prices are rounded to the nearest thousand.
 */
export function computeBandRecommendationPrices(band: {
  min: number;
  max: number;
}): BandRecommendationPrices {
  const midRaw = (band.min + band.max) / 2;
  const lowestTypicalPrice = roundToNearestThousand(band.min);
  const midBandPrice = roundToNearestThousand(midRaw);
  const fasterSalePrice = roundToNearestThousand(
    midRaw * (1 - SELLER_BAND_QUICK_SALE_DISCOUNT_RATE),
  );

  return {
    midBandPrice,
    fasterSalePrice,
    lowestTypicalPrice,
  };
}

/**
 * Chips sorted by price high → low; lowest price tier gets recommended flag.
 */
export function getOrderedBandPriceChips(
  prices: BandRecommendationPrices,
): BandPriceChipOption[] {
  const chips: BandPriceChipOption[] = [
    { tier: "standard", price: prices.midBandPrice, isRecommended: false },
    {
      tier: "sell_sooner",
      price: prices.lowestTypicalPrice,
      isRecommended: false,
    },
    {
      tier: "sell_fastest",
      price: prices.fasterSalePrice,
      isRecommended: false,
    },
  ];

  const sorted = [...chips].sort((a, b) => b.price - a.price);
  const lowestPrice = sorted[sorted.length - 1]?.price ?? 0;
  const atLowest = sorted.filter((c) => c.price === lowestPrice);
  const recommendedTier =
    RECOMMENDED_TIER_PRIORITY.find((t) => atLowest.some((c) => c.tier === t)) ??
    atLowest[0]?.tier;

  return sorted.map((chip) => ({
    ...chip,
    isRecommended: chip.tier === recommendedTier,
  }));
}

/** Price on the chip marked Recommended (recovery target when over ceiling). */
export function getRecommendedBandChipPrice(band: {
  min: number;
  max: number;
}): number {
  const chips = getOrderedBandPriceChips(computeBandRecommendationPrices(band));
  const recommended = chips.find((chip) => chip.isRecommended);
  return recommended?.price ?? chips[chips.length - 1]?.price ?? band.min;
}
